import { ICellEditorComp, ICellEditorParams } from "ag-grid-enterprise";

// on_off: independent checkboxes. pick_one: radios, at most one token.
export type ChecklistGroupKind = "on_off" | "pick_one";

export interface ChecklistGroup {
  key: string;
  label: string;
  kind: ChecklistGroupKind;
  // Consecutive groups sharing a section render under one heading.
  section?: string;
  tokens: string[];
}

export interface ListParams {
  separator: string;
  labels: Record<string, string>;
  groups: ChecklistGroup[];
  // Canonical token => other spellings that tick it (and re-emit the canonical token).
  aliases?: Record<string, string[]>;
  // Tokens preselected in any pick_one group the cell leaves empty (the wizard defaults).
  defaults?: string[];
}

export interface ChecklistCellEditorParams extends ICellEditorParams {
  list: ListParams;
}

export interface ChecklistState {
  // Ticked on_off tokens.
  checked: Set<string>;
  // Ticked tokens per pick_one group key. Normally 0 or 1; a pasted cell may hold several,
  // and all are kept so an untouched cell still serialises unchanged.
  picks: Record<string, string[]>;
  // Tokens no group knows. Kept verbatim so the server's validation still reports them.
  unknown: string[];
}

// Same normalisation as the server's Tokens.parse, so a token ticks here iff it would there.
function normaliseToken(raw: string): string {
  return raw.trim().toLowerCase().replace(/[\s-]+/g, "_");
}

export function parseChecklist(
  raw: string | null | undefined,
  separator: string,
  groups: ChecklistGroup[],
  aliases: Record<string, string[]> = {}
): ChecklistState {
  const state: ChecklistState = { checked: new Set(), picks: {}, unknown: [] };
  groups.forEach((group) => {
    if (group.kind === "pick_one") state.picks[group.key] = [];
  });

  // Normalised form -> canonical token and its group; vocabulary tokens with spaces ("french (canada)") still match.
  const known = new Map<string, { token: string; group: ChecklistGroup }>();
  groups.forEach((group) => group.tokens.forEach((token) => known.set(normaliseToken(token), { token, group })));
  // After the tokens, and never over one: a canonical spelling always wins.
  groups.forEach((group) =>
    group.tokens.forEach((token) =>
      (aliases[token] ?? []).forEach((alias) => {
        if (!known.has(normaliseToken(alias))) known.set(normaliseToken(alias), { token, group });
      })
    )
  );

  const seen = new Set<string>();
  String(raw ?? "")
    .split(separator)
    .map((token) => token.trim())
    .filter((token) => token !== "")
    .forEach((raw) => {
      const normalised = normaliseToken(raw);
      if (seen.has(normalised)) return;
      seen.add(normalised);

      const match = known.get(normalised);
      if (!match) state.unknown.push(raw);
      else if (match.group.kind === "pick_one") state.picks[match.group.key].push(match.token);
      else state.checked.add(match.token);
    });

  return state;
}

// Group order, then unknowns.
export function serializeChecklist(state: ChecklistState, groups: ChecklistGroup[], separator: string): string {
  const out: string[] = [];
  groups.forEach((group) => {
    const ticked = group.kind === "pick_one" ? state.picks[group.key] ?? [] : [...state.checked];
    group.tokens.forEach((token) => ticked.includes(token) && out.push(token));
  });

  return [...out, ...state.unknown].join(separator);
}

// Gives every empty pick_one group its default token; a group the cell already picks in is untouched.
export function applyDefaults(state: ChecklistState, groups: ChecklistGroup[], defaults: string[] = []): void {
  groups.forEach((group) => {
    if (group.kind !== "pick_one" || (state.picks[group.key] ?? []).length > 0) return;
    const token = defaults.find((candidate) => group.tokens.includes(candidate));
    if (token) state.picks[group.key] = [token];
  });
}

let instanceSeq = 0;

interface ItemRef {
  token: string;
  group: ChecklistGroup;
  input: HTMLInputElement;
}

// One filterable line: a whole pick_one group, or a single on_off / ungrouped item.
interface RowRef {
  el: HTMLElement;
  needle: string;
}

interface SectionRef {
  name?: string;
  el: HTMLElement;
  rows: RowRef[];
}

function buildSection(group: ChecklistGroup): SectionRef {
  const el = document.createElement("div");
  el.className = "checklist-cell-editor__group";

  const heading = document.createElement("div");
  heading.className = "checklist-cell-editor__heading";
  heading.textContent = group.section ?? group.label;
  el.appendChild(heading);

  return { name: group.section, el, rows: [] };
}

function buildAction(text: string, onClick: () => void): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "checklist-cell-editor__action";
  button.textContent = text;
  button.addEventListener("click", onClick);
  return button;
}

/**
 * Popup checklist for pipe-separated list columns. Driven entirely by the server's `list`
 * params: groups, labels and separator. The search box only filters; it is never part of the
 * value, which is rebuilt from the ticked items on commit.
 */
export class ChecklistCellEditor implements ICellEditorComp<unknown, string> {
  private params!: ChecklistCellEditorParams;
  private list!: ListParams;
  private state!: ChecklistState;
  private container!: HTMLDivElement;
  private search!: HTMLInputElement;
  private body!: HTMLDivElement;
  private noMatches!: HTMLDivElement;
  private preview!: HTMLSpanElement;
  private cancelButton!: HTMLButtonElement;
  private saveButton!: HTMLButtonElement;
  // Only Save sets this; every other way editing ends (Cancel, Escape, click-off, focus loss) discards the edit.
  private saving = false;
  private sections: SectionRef[] = [];
  private items: ItemRef[] = [];
  private readonly instanceId = ++instanceSeq;

  private searchHandler = (): void => this.applyFilter();
  private keydownHandler = (event: KeyboardEvent): void => this.handleKeyDown(event);

  init(params: ChecklistCellEditorParams): void {
    this.params = params;
    this.list = params.list;
    this.state = parseChecklist(params.value as string | null | undefined, this.list.separator, this.list.groups, this.list.aliases);
    applyDefaults(this.state, this.list.groups, this.list.defaults);

    this.container = document.createElement("div");
    this.container.className = "checklist-cell-editor";
    // Under document.body (isPopup) no --ag-* var resolves, so the cell's type is copied across.
    const cellRect = params.eGridCell.getBoundingClientRect();
    const cellStyle = window.getComputedStyle(params.eGridCell);
    this.container.style.fontFamily = cellStyle.fontFamily;
    this.container.style.fontSize = cellStyle.fontSize;
    this.container.style.setProperty("--checklist-cell-editor-row-height", `${cellRect.height}px`);
    this.container.style.setProperty("--checklist-cell-editor-padding", cellStyle.paddingLeft);

    this.search = document.createElement("input");
    this.search.type = "text";
    this.search.className = "checklist-cell-editor__search";
    this.search.placeholder = "Search…";
    this.search.autocomplete = "off";
    this.search.setAttribute("aria-label", "Search");
    this.search.addEventListener("input", this.searchHandler);

    this.body = document.createElement("div");
    this.body.className = "checklist-cell-editor__body";
    this.buildGroups();

    this.noMatches = document.createElement("div");
    this.noMatches.className = "checklist-cell-editor__notice";
    this.noMatches.textContent = "No matches";
    this.noMatches.hidden = true;
    this.body.appendChild(this.noMatches);

    this.container.append(this.search, this.body);
    this.container.appendChild(this.buildFooter());
    this.container.addEventListener("keydown", this.keydownHandler);

    this.syncInputs();
  }

  getGui(): HTMLElement {
    return this.container;
  }

  getValue(): string {
    return serializeChecklist(this.state, this.list.groups, this.list.separator);
  }

  isCancelAfterEnd(): boolean {
    return !this.saving;
  }

  afterGuiAttached(): void {
    this.search.focus();
  }

  isPopup(): boolean {
    return true;
  }

  getPopupPosition(): "under" {
    return "under";
  }

  destroy(): void {
    this.search.removeEventListener("input", this.searchHandler);
    this.container.removeEventListener("keydown", this.keydownHandler);
  }

  // ── Rendering ──────────────────────────────────────────────────────────

  private buildGroups(): void {
    let current: SectionRef | undefined;
    this.list.groups.forEach((group) => {
      // A group with no section keeps its own heading, so it never merges with its neighbours.
      const section = current && group.section && group.section === current.name ? current : buildSection(group);
      if (section !== current) {
        this.sections.push(section);
        this.body.appendChild(section.el);
        current = section;
      }

      const rows = group.kind === "pick_one" && group.section ? [this.buildPickOneRow(group)] : this.buildItemRows(group);
      rows.forEach((row) => section.el.appendChild(row.el));
      section.rows.push(...rows);
    });
  }

  // Group label left, radios in fixed-width columns right so options line up across rows.
  private buildPickOneRow(group: ChecklistGroup): RowRef {
    const el = document.createElement("div");
    el.className = "checklist-cell-editor__row";
    el.setAttribute("role", "radiogroup");
    el.setAttribute("aria-label", group.label);

    const label = document.createElement("span");
    label.className = "checklist-cell-editor__row-label";
    label.textContent = group.label;

    const options = document.createElement("div");
    options.className = "checklist-cell-editor__options";
    group.tokens.forEach((token) => options.appendChild(this.buildInput(group, token, "checklist-cell-editor__option")));

    el.append(label, options);
    return { el, needle: this.needleFor(group, group.tokens) };
  }

  private buildItemRows(group: ChecklistGroup): RowRef[] {
    return group.tokens.map((token) => ({
      el: this.buildInput(group, token, "checklist-cell-editor__item"),
      needle: this.needleFor(group, [token]),
    }));
  }

  // Group label, token labels and the raw tokens are matched; a typed token still finds its row.
  private needleFor(group: ChecklistGroup, tokens: string[]): string {
    return [group.label, ...tokens.map((token) => this.list.labels[token] ?? token), ...tokens].join("\n").toLowerCase();
  }

  private buildInput(group: ChecklistGroup, token: string, className: string): HTMLLabelElement {
    const label = this.list.labels[token] ?? token;

    const row = document.createElement("label");
    row.className = className;

    const input = document.createElement("input");
    input.type = group.kind === "pick_one" ? "radio" : "checkbox";
    if (group.kind === "pick_one") input.name = `checklist-cell-editor-${this.instanceId}-${group.key}`;
    input.addEventListener("change", () => this.handleToggle(group, token, input.checked));

    const text = document.createElement("span");
    text.textContent = label;

    row.append(input, text);
    this.items.push({ token, group, input });
    return row;
  }

  private buildFooter(): HTMLElement {
    const footer = document.createElement("div");
    footer.className = "checklist-cell-editor__footer";

    this.preview = document.createElement("span");
    this.preview.className = "checklist-cell-editor__preview";

    this.cancelButton = buildAction("Cancel", () => this.params.stopEditing(true));
    this.saveButton = buildAction("Save", () => this.save());
    this.saveButton.classList.add("checklist-cell-editor__action--primary");

    footer.append(this.preview, this.cancelButton, this.saveButton);
    return footer;
  }

  // ── State ────────────────────────────────────────────────────────────────

  private handleToggle(group: ChecklistGroup, token: string, on: boolean): void {
    if (group.kind === "pick_one") {
      if (on) this.state.picks[group.key] = [token];
    } else if (on) {
      this.state.checked.add(token);
    } else {
      this.state.checked.delete(token);
    }
    this.syncInputs();
  }

  private save(): void {
    this.saving = true;
    this.params.stopEditing(true);
  }

  private syncInputs(): void {
    this.items.forEach(({ token, group, input }) => {
      input.checked =
        group.kind === "pick_one" ? (this.state.picks[group.key] ?? [])[0] === token : this.state.checked.has(token);
    });
    const value = this.getValue();
    this.preview.textContent = value === "" ? "(blank)" : value;
    this.preview.title = value;
  }

  // ── Filtering ────────────────────────────────────────────────────────────

  private applyFilter(): void {
    const query = this.search.value.trim().toLowerCase();
    this.sections.forEach((section) => {
      section.rows.forEach((row) => {
        row.el.hidden = query !== "" && !row.needle.includes(query);
      });
      section.el.hidden = section.rows.every((row) => row.el.hidden);
    });
    this.noMatches.hidden = this.sections.some((section) => !section.el.hidden);
  }

  // ── Keyboard ─────────────────────────────────────────────────────────────

  private handleKeyDown(event: KeyboardEvent): void {
    // Escape alone is left to AG-Grid's cancel; every other key stays inside the popup so the
    // grid never navigates, clears or copies cells underneath it.
    if (event.key === "Escape") return;
    event.stopPropagation();

    if (event.key === "Enter") {
      event.preventDefault();
      if (event.target === this.cancelButton) this.params.stopEditing(true);
      else this.save();
      return;
    }

    if (event.key === "Tab") this.trapTab(event);
  }

  private trapTab(event: KeyboardEvent): void {
    const focusable = [this.search, ...this.items.map((item) => item.input), this.cancelButton, this.saveButton].filter(
      (el) => !el.closest("[hidden]") && !el.disabled
    );
    if (focusable.length === 0) return;

    // Radios share a tab stop per group, so only wrap at the ends; the browser handles the middle.
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }
}
